import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { formatDateTimeDDMMYYYY } from '../utils/dateUtils';

const AdminLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [showClearDialog, setShowClearDialog] = useState(false);
  const BASEURL = process.env.REACT_APP_API_URL;

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const url = filterType === 'all' 
        ? `${BASEURL}/admin/logs`
        : `${BASEURL}/admin/logs?type=${filterType}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }

      const data = await response.json();
      setLogs(data.logs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filterType]);

  const clearLogs = async () => {
    try {
      const response = await fetch(`${BASEURL}/admin/logs/clear`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to clear logs');
      }

      setShowClearDialog(false);
      fetchLogs();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTimeDDMMYYYY(dateString);
  };

  const getLogIcon = (type) => {
    switch (type) {
      case 'user_action':
        return <span>ℹ️</span>;
      case 'system_event':
        return <span>✅</span>;
      case 'error':
        return <span>❌</span>;
      default:
        return <span>ℹ️</span>;
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'user_action':
        return 'info';
      case 'system_event':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }



  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography 
          variant="h4" 
          component="h2"
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: 700
          }}
        >
          System Logs
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ color: '#666' }}>Filter</InputLabel>
            <Select
              value={filterType}
              label="Filter"
              onChange={(e) => setFilterType(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ddd'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#667eea'
                }
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="user_action">User Actions</MenuItem>
              <MenuItem value="system_event">System Events</MenuItem>
              <MenuItem value="error">Errors</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title="Refresh">
            <button 
              onClick={fetchLogs} 
              style={{ 
                padding: '0.75rem 1.5rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(40, 167, 69, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(40, 167, 69, 0.3)';
              }}
            >
              🔄 Refresh
            </button>
          </Tooltip>
          
          <Tooltip title="Clear All Logs">
            <button 
              onClick={() => setShowClearDialog(true)} 
              style={{ 
                padding: '0.75rem 1.5rem', 
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: logs.length === 0 ? 'not-allowed' : 'pointer',
                opacity: logs.length === 0 ? 0.5 : 1,
                fontSize: '0.9rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease',
                boxShadow: logs.length === 0 ? 'none' : '0 2px 8px rgba(220, 53, 69, 0.3)'
              }}
              disabled={logs.length === 0}
              onMouseEnter={(e) => {
                if (logs.length > 0) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (logs.length > 0) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(220, 53, 69, 0.3)';
                }
              }}
            >
              🗑️ Clear
            </button>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ 
        backgroundColor: 'white', 
        borderRadius: 2, 
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Action/Event</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log, index) => (
                <TableRow key={index} hover sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span style={{ fontSize: '1.2rem' }}>{getLogIcon(log.type)}</span>
                      <Chip
                        label={log.type.replace('_', ' ')}
                        size="small"
                        color={getLogColor(log.type)}
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'monospace' }}>
                      {formatDate(log.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                      {log.user_email || log.user_id || 'System'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                      {log.action || log.event_type || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300, color: '#666', lineHeight: 1.4 }}>
                      {log.details ? 
                        (typeof log.details === 'string' ? log.details : JSON.stringify(log.details)) : 
                        'No details'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {logs.length === 0 && (
        <Box textAlign="center" py={6}>
          <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
            📋 No logs found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            System activity will appear here as you use the application
          </Typography>
        </Box>
      )}

      <Box mt={3} textAlign="center">
        <Typography variant="body2" color="textSecondary">
          Total Logs: <strong>{logs.length}</strong>
        </Typography>
      </Box>

      {/* Clear Logs Dialog */}
      <Dialog open={showClearDialog} onClose={() => setShowClearDialog(false)}>
        <DialogTitle sx={{ pb: 1 }}>Clear All Logs</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#666' }}>
            Are you sure you want to clear all system logs? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={() => setShowClearDialog(false)}
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={clearLogs} 
            color="error" 
            variant="contained"
            sx={{ 
              backgroundColor: '#dc3545',
              '&:hover': { backgroundColor: '#c82333' }
            }}
          >
            Clear All Logs
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLogs; 